import type { ReactNode } from "react";

import { Button } from "../../../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../../../../components/ui/dialog";
import type { ChangedFieldRow } from "./callScriptComparison";

// ---------------------------------------------------------------------------
// CallScriptConfirmSaveDialog
//
// The "CAPS WARNING" confirmation both Call Scripts show before saving, when
// the analyst's "Verified Information" differs from what's saved: a
// three-column Verified-vs-Saved table listing ONLY the changed fields. When
// nothing changed, callers save straight through without opening this.
//
// Strings come in as props so neither screen's i18n namespace leaks in.
// ---------------------------------------------------------------------------

/** The dialog's copy, resolved by the caller from its own i18n namespace. */
export interface CallScriptConfirmSaveLabels {
  title: string;
  message: string;
  question: string;
  verifiedInformation: string;
  savedInformation: string;
  confirm: string;
  cancel: string;
}

interface CallScriptConfirmSaveContentProperties<TKey extends string> {
  /** Rendered as the heading — a DialogTitle in the popup, a plain heading when inlined as a step. */
  readonly title: ReactNode;
  /** Only the fields that differ — see `buildChangedFieldRows`. */
  readonly rows: ReadonlyArray<ChangedFieldRow<TKey>>;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** Disables both buttons while the save (or its pre-flight checks) is in flight. */
  readonly disabled?: boolean;
  /** Surfaced above the buttons — the employment script uses this for a failed force-exit re-check. */
  readonly error?: string;
  readonly labels: CallScriptConfirmSaveLabels;
}

// The confirmation body without dialog chrome, so the residence script can
// render it as a step inside its own modal.
export function CallScriptConfirmSaveContent<TKey extends string>({
  title,
  rows,
  onConfirm,
  onCancel,
  disabled = false,
  error,
  labels,
}: CallScriptConfirmSaveContentProperties<TKey>) {
  return (
    <div className="gap-(--spacing-4) space-y-(--spacing-4)">
      <div>{title}</div>
      <p className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
        {labels.message}
      </p>
      <p className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
        {labels.question}
      </p>
      {rows.length > 0 && (
        <div className="border-border max-h-64 overflow-y-auto rounded-lg border">
          <div className="gap-x-(--spacing-2) gap-y-(--spacing-1) p-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) grid grid-cols-[1fr_1fr_1fr]">
            <div />
            <div className="text-primary font-(--font-weight-semibold)">{labels.verifiedInformation}</div>
            <div className="text-primary font-(--font-weight-semibold)">{labels.savedInformation}</div>
            {rows.map((row) => (
              <div key={row.key} className="contents">
                <div className="text-primary font-(--font-weight-medium)">{row.label}</div>
                <div className="break-words">{row.verifiedDisplay}</div>
                <div className="text-muted-foreground break-words">{row.savedDisplay}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {error && (
        <p
          role="alert"
          className="text-destructive text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
        >
          {error}
        </p>
      )}
      <div className="gap-(--spacing-2) flex justify-end">
        <Button type="button" variant="outline" size="xs" disabled={disabled} onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button type="button" size="xs" disabled={disabled} onClick={onConfirm}>
          {labels.confirm}
        </Button>
      </div>
    </div>
  );
}

interface CallScriptConfirmSaveDialogProperties<TKey extends string> extends Omit<
  CallScriptConfirmSaveContentProperties<TKey>,
  "title" | "onCancel"
> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CallScriptConfirmSaveDialog<TKey extends string>({
  open,
  onOpenChange,
  labels,
  ...contentProperties
}: CallScriptConfirmSaveDialogProperties<TKey>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <CallScriptConfirmSaveContent
          {...contentProperties}
          labels={labels}
          onCancel={() => onOpenChange(false)}
          title={
            <>
              <DialogTitle className="text-(length:--typography-base-sizes-large-font-size)/(--typography-base-sizes-large-line-height) font-(--font-weight-semibold)">
                {labels.title}
              </DialogTitle>
              <DialogDescription className="sr-only">{labels.title}</DialogDescription>
            </>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
